import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { visitorAPI, noticeAPI, facilityAPI, paymentAPI, gatePassAPI, staffAPI, invoiceAPI, receiptAPI, notificationAPI, profileAPI, communityAPI, surveyAPI, chatAPI, recommendationAPI } from '../services/api';
import { toast } from 'sonner';
import { getStoredUser } from '../services/auth';
import { useCommunityConfig } from '../services/communityConfig';

import { ResidentHeader } from '../components/resident/ResidentHeader';
import { ResidentTiles } from '../components/resident/ResidentTiles';
import { GatePassSection, GatePassModals } from '../components/resident/GatePassSection';
import { GatePassPickerModal, QuickPassModal, DeliveryPassModal, FrequentPassModal, PartyInviteModal } from '../components/resident/GatePassWizards';
import { SurveysTile, ChatTile, SurveysModal, ChatModal } from '../components/resident/SocialModals';
import { RecommendationsTile, RecommendationsModal } from '../components/resident/RecommendationsModal';
import { NoticesSection, NoticeModals } from '../components/resident/NoticesSection';
import { FacilitiesModal, BookingModal } from '../components/resident/FacilitiesModal';
import { PaymentModal, UPIPaymentModal, ReceiptsModal, InvoicesModal, PaymentsDueModal } from '../components/resident/PaymentModals';
import { VisitorsModal, StaffModal, AddCommunityModal } from '../components/resident/InfoModals';
import { MCListModal, NeighborsDirectoryModal } from '../components/resident/CommunityDirectoryModals';
import { TrendingSurveyCard } from '../components/resident/TrendingSurveyCard';
import { DeliveryArrivalToast } from '../components/resident/DeliveryArrivalToast';
import { HelpdeskModal } from '../components/helpdesk/HelpdeskModal';

function relativeTime(iso) {
  if (!iso) return '';
  try {
    const ms = Date.now() - new Date(iso).getTime();
    if (ms < 60_000) return 'just now';
    const m = Math.floor(ms / 60_000);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    if (d < 7) return `${d}d ago`;
    return new Date(iso).toLocaleDateString();
  } catch { return ''; }
}

export default function ResidentDashboard() {
  const navigate = useNavigate();
  const [user] = useState(getStoredUser());
  const { config } = useCommunityConfig();
  const [visitors, setVisitors] = useState([]);
  const [notices, setNotices] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [payments, setPayments] = useState([]);
  const [gatePasses, setGatePasses] = useState([]);
  const [myStaff, setMyStaff] = useState([]);
  const [activeStaffOnCampus, setActiveStaffOnCampus] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modal visibility state
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showPreApprovalModal, setShowPreApprovalModal] = useState(false);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [showUPIModal, setShowUPIModal] = useState(false);
  const [showReceiptsModal, setShowReceiptsModal] = useState(false);
  const [showVisitorsModal, setShowVisitorsModal] = useState(false);
  const [showNoticesModal, setShowNoticesModal] = useState(false);
  const [showPaymentsDueModal, setShowPaymentsDueModal] = useState(false);
  const [showStaffModal, setShowStaffModal] = useState(false);
  const [showInvoicesModal, setShowInvoicesModal] = useState(false);
  const [showFacilitiesModal, setShowFacilitiesModal] = useState(false);
  const [showAddCommunityModal, setShowAddCommunityModal] = useState(false);
  const [showMCModal, setShowMCModal] = useState(false);
  const [showNeighborsModal, setShowNeighborsModal] = useState(false);
  const [mcMembersCount, setMcMembersCount] = useState(0);
  const [neighborsCount, setNeighborsCount] = useState(0);
  const [showSurveysModal, setShowSurveysModal] = useState(false);
  const [showChatModal, setShowChatModal] = useState(false);
  const [showHelpdeskModal, setShowHelpdeskModal] = useState(false);
  const [openSurveyCount, setOpenSurveyCount] = useState(0);
  const [chatUnread, setChatUnread] = useState(0);
  const [chatRecent, setChatRecent] = useState('');
  const [showRecommendationsModal, setShowRecommendationsModal] = useState(false);
  const [recommendationsCount, setRecommendationsCount] = useState(0);
  const [recRecent, setRecRecent] = useState('');

  // Selection state
  const [selectedGatePass, setSelectedGatePass] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [selectedFacility, setSelectedFacility] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [selectedNotice, setSelectedNotice] = useState(null);
  const [upiLinks, setUpiLinks] = useState(null);
  const [paymentProcessing, setPaymentProcessing] = useState(false);

  // Form state
  const [preApprovalForm, setPreApprovalForm] = useState({ visitor_name: '', visitor_phone: '', valid_hours: 1 });
  const [bookingForm, setBookingForm] = useState({ date: '', checkout_date: '', slot: '', comments: '' });
  const [invoiceFilter, setInvoiceFilter] = useState({ month: '', year: '' });
  const [receiptFilter, setReceiptFilter] = useState({ month: '', year: '' });

  // Community state
  const [communityName, setCommunityName] = useState('');
  const [communitySearch, setCommunitySearch] = useState('');
  const [communityResults, setCommunityResults] = useState([]);
  const [bookingNotifications, setBookingNotifications] = useState([]);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    loadData();
    loadCommunityName();
    const interval = setInterval(() => loadData(), 30000);
    return () => clearInterval(interval);
  }, []);
  /* eslint-enable react-hooks/exhaustive-deps */

  const loadCommunityName = async () => {
    try {
      const { data } = await profileAPI.getCommunities();
      setCommunityName(data?.current?.name || 'Resident Portal');
    } catch { setCommunityName('Resident Portal'); }
  };

  const handleSearchCommunity = async (q) => {
    setCommunitySearch(q);
    if (q.length < 1) { setCommunityResults([]); return; }
    try { const { data } = await profileAPI.searchCommunities(q); setCommunityResults(data); }
    catch { setCommunityResults([]); }
  };

  const handleRequestJoinCommunity = async (communityId) => {
    try {
      await profileAPI.requestCommunityJoin(communityId);
      toast.success('Join request sent! Awaiting management approval.');
      setCommunitySearch(''); setCommunityResults([]);
      setShowAddCommunityModal(false);
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to send request'); }
  };

  const generateQRData = (gatePass) => {
    return JSON.stringify({
      type: 'GATE_PASS',
      code: gatePass.access_code,
      visitor: gatePass.visitor_name,
      phone: gatePass.visitor_phone,
      villa: user?.villa_number,
      resident: user?.name,
      valid_until: gatePass.valid_until,
      pass_id: gatePass.id
    });
  };

  const downloadQRCode = async () => {
    const card = document.getElementById('gate-pass-card');
    if (!card || !selectedGatePass) return;
    try {
      const canvas = await import('html2canvas').then(m => m.default(card, {
        backgroundColor: '#ffffff', scale: 2, useCORS: true,
      }));
      const link = document.createElement('a');
      link.download = `gate-pass-${selectedGatePass.access_code}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (e) {
      process.env.NODE_ENV !== 'production' && console.error('Gate pass download failed:', e);
      toast.error('Download failed');
    }
  };

  const shareGatePass = async () => {
    if (!selectedGatePass) return;
    const card = document.getElementById('gate-pass-card');
    const shareText = `Gate Pass for ${selectedGatePass.visitor_name}\n\nVilla: ${user?.villa_number}\nHost: ${user?.name}\nAccess Code: ${selectedGatePass.access_code}\nValid Until: ${new Date(selectedGatePass.valid_until).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}\n\nShow this code at the gate.`;

    // Try sharing as image (if Web Share API level 2 is available)
    if (navigator.share && card) {
      try {
        const canvas = await import('html2canvas').then(m => m.default(card, {
          backgroundColor: '#ffffff', scale: 2, useCORS: true,
        }));
        const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
        const file = new File([blob], `gate-pass-${selectedGatePass.access_code}.png`, { type: 'image/png' });
        await navigator.share({ title: 'Gate Pass', text: shareText, files: [file] });
        return;
      } catch (err) {
        // Fallback to text-only share
        try { await navigator.share({ title: 'Gate Pass', text: shareText }); return; }
        catch (shareErr) { process.env.NODE_ENV !== 'production' && console.error('Share failed:', shareErr); }
      }
    }
    copyToClipboard(shareText);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Gate pass details copied to clipboard!');
  };

  const loadData = async () => {
    try {
      const [visitorsRes, noticesRes, facilitiesRes, paymentsRes, gatePassesRes, myStaffRes, invoicesRes, receiptsRes, notifRes] = await Promise.all([
        visitorAPI.getAll(user.villa_number),
        noticeAPI.getAll(),
        facilityAPI.getAll(),
        paymentAPI.getAll(user.villa_number),
        gatePassAPI.getAll(user.villa_number),
        staffAPI.getMyStaff(),
        invoiceAPI.getAll(),
        receiptAPI.getAll(),
        notificationAPI.getAll()
      ]);
      setVisitors(visitorsRes.data);
      setNotices(noticesRes.data);
      setFacilities(facilitiesRes.data);
      setPayments(paymentsRes.data);
      setGatePasses(gatePassesRes.data);
      setMyStaff(myStaffRes.data.staff || []);
      setActiveStaffOnCampus(myStaffRes.data.active_on_campus || []);
      setInvoices(invoicesRes.data || []);
      setReceipts(receiptsRes.data || []);
      const allNotifs = notifRes.data?.notifications || [];
      const today = new Date().toISOString().split('T')[0];
      setBookingNotifications(
        allNotifs
          .filter(n => n.type === 'booking_approved' || n.type === 'booking_rejected')
          .filter(n => { const checkoutDate = n.metadata?.checkout_date; if (!checkoutDate) return true; return checkoutDate >= today; })
      );
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Separate effect so these reload when config becomes available
  useEffect(() => {
    let cancelled = false;
    const loadCommunityDirectory = async () => {
      if (config?.mc_list_enabled) {
        try {
          const res = await communityAPI.getMCMembers();
          if (!cancelled) setMcMembersCount((res.data || []).length);
        } catch { /* silent */ }
      } else {
        setMcMembersCount(0);
      }
      if (config?.neighbors_directory_enabled) {
        try {
          const res = await communityAPI.getDirectory();
          if (!cancelled) setNeighborsCount((res.data || []).length);
        } catch { /* silent */ }
      } else {
        setNeighborsCount(0);
      }

      // Surveys + chat counts (always load)
      try {
        const sres = await surveyAPI.listForResident();
        if (!cancelled) setOpenSurveyCount((sres.data || []).filter(s => !s.responded).length);
      } catch { /* silent */ }
      try {
        const cres = await chatAPI.threads();
        const threads = cres.data || [];
        if (!cancelled) {
          setChatUnread(threads.reduce((sum, t) => sum + (t.unread_count || 0), 0));
          // Recent activity preview: latest thread with a message
          const withMsg = threads.filter(t => t.last_message_at).sort((a, b) =>
            new Date(b.last_message_at) - new Date(a.last_message_at)
          );
          if (withMsg.length > 0) {
            const t = withMsg[0];
            setChatRecent(`Latest: ${t.display_name || 'Thread'} · ${relativeTime(t.last_message_at)}`);
          } else if (threads.length > 0) {
            setChatRecent(`${threads.length} active conversation${threads.length === 1 ? '' : 's'}`);
          } else {
            setChatRecent('');
          }
        }
      } catch { /* silent */ }
      try {
        const rres = await recommendationAPI.list();
        const recs = rres.data || [];
        if (!cancelled) {
          setRecommendationsCount(recs.length);
          // Show top-rated business if any has ratings, else recent additions
          const rated = recs.filter(r => (r.rating_count || 0) > 0)
            .sort((a, b) => (b.rating_avg || 0) - (a.rating_avg || 0));
          if (rated.length > 0) {
            const top = rated[0];
            setRecRecent(`Top: ${top.business_name} · ★${(top.rating_avg || 0).toFixed(1)}`);
          } else if (recs.length > 0) {
            // Show count of new ones from last 7 days
            const weekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
            const newCount = recs.filter(r => r.created_at && new Date(r.created_at).getTime() > weekAgo).length;
            setRecRecent(newCount > 0 ? `${newCount} new this week` : 'Tap to browse trusted picks');
          } else {
            setRecRecent('');
          }
        }
      } catch { /* silent */ }
    };
    loadCommunityDirectory();
    return () => { cancelled = true; };
  }, [config?.mc_list_enabled, config?.neighbors_directory_enabled]);

  const handleMockPayment = async (payment) => { setSelectedPayment(payment); setShowPaymentModal(true); };

  const processPayment = async () => {
    try {
      await paymentAPI.markPaid(selectedPayment.id);
      toast.success('Payment successful!');
      setShowPaymentModal(false);
      loadData();
    } catch { toast.error('Payment failed'); }
  };

  const handleCreatePreApproval = async () => {
    if (!preApprovalForm.visitor_name || !preApprovalForm.visitor_phone) { toast.error('Please fill all fields'); return; }
    try {
      const { data } = await gatePassAPI.create({
        visitor_name: preApprovalForm.visitor_name,
        visitor_phone: preApprovalForm.visitor_phone,
        villa_number: user.villa_number,
        valid_hours: parseFloat(preApprovalForm.valid_hours),
        pass_type: 'pre_approval'
      });
      toast.success(`Gate pass generated! Access code: ${data.access_code}`, { duration: 8000 });
      setShowPreApprovalModal(false);
      setPreApprovalForm({ visitor_name: '', visitor_phone: '', valid_hours: 1 });
      loadData();
    } catch { toast.error('Failed to generate gate pass'); }
  };

  const handleBookFacility = (facility) => {
    setBookingForm({ date: '', checkout_date: '', slot: '', comments: '' });
    setSelectedFacility(facility);
    setShowBookingModal(true);
  };

  const handleCloseBookingModal = (open) => {
    if (!open) { setSelectedFacility(null); setBookingForm({ date: '', checkout_date: '', slot: '', comments: '' }); }
    setShowBookingModal(open);
  };

  const handleConfirmBooking = async () => {
    const isDateRange = selectedFacility?.booking_type !== 'slot';
    if (!bookingForm.date) { toast.error('Please select check-in date'); return; }
    if (isDateRange && !bookingForm.checkout_date) { toast.error('Please select check-out date'); return; }
    if (!isDateRange && !bookingForm.slot) { toast.error('Please select time slot'); return; }
    try {
      await facilityAPI.book(selectedFacility.id, { date: bookingForm.date, checkout_date: bookingForm.checkout_date || bookingForm.date, slot: bookingForm.slot, comments: bookingForm.comments });
      toast.success('Booking request submitted! Confirmation will be sent to your email upon MC approval.', { duration: 6000 });
      handleCloseBookingModal(false);
      loadData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to book facility'); }
  };

  const handlePayInvoice = async (invoice) => {
    try {
      const response = await invoiceAPI.getUPILinks(invoice.id);
      setSelectedInvoice(invoice);
      setUpiLinks(response.data);
      setShowUPIModal(true);
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to generate payment links'); }
  };

  const openUPIApp = (url) => { window.location.href = url; };

  const handleConfirmUPIPayment = async () => {
    if (!selectedInvoice || !upiLinks) return;
    setPaymentProcessing(true);
    try {
      await invoiceAPI.confirmPayment(selectedInvoice.id, 'UPI', upiLinks.transaction_id);
      toast.success('Payment confirmed! Receipt generated.', { duration: 5000 });
      setShowUPIModal(false); setSelectedInvoice(null); setUpiLinks(null);
      loadData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to confirm payment'); }
    finally { setPaymentProcessing(false); }
  };

  const copyUPIId = () => {
    if (upiLinks?.upi_vpa) { navigator.clipboard.writeText(upiLinks.upi_vpa); toast.success('UPI ID copied to clipboard!'); }
  };

  const handleLogout = () => { localStorage.clear(); sessionStorage.clear(); navigate('/'); };

  // Derived data
  const activeGatePasses = gatePasses.filter(p => p.status === 'active' && new Date(p.valid_until) > new Date());
  const pendingApprovalPasses = gatePasses.filter(p => p.status === 'pending_approval' && new Date(p.valid_until) > new Date());

  const handleApproveGatePass = async (passId) => {
    try {
      await gatePassAPI.approve(passId);
      toast.success('Visitor approved and entry recorded');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve');
    }
  };

  const handleRejectGatePass = async (passId) => {
    try {
      await gatePassAPI.reject(passId);
      toast.success('Gate pass rejected');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reject');
    }
  };
  const pendingInvoices = invoices.filter(i => i.status === 'pending' || i.status === 'overdue');
  const paidInvoices = invoices.filter(i => i.status === 'paid');
  const totalDue = pendingInvoices.reduce((sum, i) => sum + i.amount, 0);

  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  const filterByMonthYear = (items, filter, dateField) => {
    let filtered = items;
    if (filter.month !== '') { filtered = filtered.filter(item => new Date(item[dateField]).getMonth() === parseInt(filter.month)); }
    if (filter.year !== '') { filtered = filtered.filter(item => new Date(item[dateField]).getFullYear() === parseInt(filter.year)); }
    return filtered;
  };

  const filteredInvoices = filterByMonthYear(invoices, invoiceFilter, 'created_at');
  const filteredReceipts = filterByMonthYear(receipts, receiptFilter, 'paid_at');

  return (
    <div className="min-h-screen bg-gk-bg">
      <ResidentHeader
        user={user}
        communityName={communityName}
        navigate={navigate}
        handleLogout={handleLogout}
        setShowAddCommunityModal={setShowAddCommunityModal}
      />

      <DeliveryArrivalToast />

      <main className="max-w-7xl mx-auto px-4 py-8">
        {loading ? (
          <div className="text-center py-20"><p className="text-slate-600">Loading...</p></div>
        ) : (
          <div className="space-y-6">
            <ResidentTiles
              visitors={visitors}
              notices={notices}
              myStaff={myStaff}
              activeStaffOnCampus={activeStaffOnCampus}
              facilities={facilities}
              bookingNotifications={bookingNotifications}
              mcMembersCount={mcMembersCount}
              neighborsCount={neighborsCount}
              setShowVisitorsModal={setShowVisitorsModal}
              setShowNoticesModal={setShowNoticesModal}
              setShowStaffModal={setShowStaffModal}
              setShowFacilitiesModal={setShowFacilitiesModal}
              setShowMCModal={setShowMCModal}
              setShowNeighborsModal={setShowNeighborsModal}
              config={config}
            />
            <TrendingSurveyCard onOpen={() => setShowSurveysModal(true)} />
            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              <ChatTile unreadCount={chatUnread} recent={chatRecent} onClick={() => setShowChatModal(true)} />
              <RecommendationsTile count={recommendationsCount} recent={recRecent} onClick={() => setShowRecommendationsModal(true)} />
              <SurveysTile count={openSurveyCount} onClick={() => setShowSurveysModal(true)} />
              {config.helpdesk_enabled && (
                <button
                  onClick={() => setShowHelpdeskModal(true)}
                  className="rounded-xl bg-white shadow-sm border border-slate-200 hover:shadow-md hover:-translate-y-0.5 transition-all p-4 text-left"
                  data-testid="resident-helpdesk-tile"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold text-slate-900">Helpdesk</p>
                      <p className="text-xs text-slate-500 mt-0.5">Raise & track tickets</p>
                    </div>
                    <div className="w-10 h-10 rounded-full bg-gk-teal/10 flex items-center justify-center">
                      <svg className="w-5 h-5 text-gk-teal" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>
                    </div>
                  </div>
                </button>
              )}
            </div>

            {config.gate_pass_enabled && (
              <GatePassSection
                activeGatePasses={activeGatePasses}
                pendingApprovalPasses={pendingApprovalPasses}
                setShowPreApprovalModal={setShowPreApprovalModal}
                user={user}
                setSelectedGatePass={setSelectedGatePass}
                setShowQRModal={setShowQRModal}
                onApprove={handleApproveGatePass}
                onReject={handleRejectGatePass}
              />
            )}

            <NoticesSection notices={notices} setSelectedNotice={setSelectedNotice} />
          </div>
        )}
      </main>

      {/* All Modals */}
      <GatePassPickerModal
        open={showPreApprovalModal === 'picker'}
        onOpenChange={(v) => setShowPreApprovalModal(v ? 'picker' : false)}
        onSelect={(t) => setShowPreApprovalModal(t)}
      />
      <QuickPassModal
        open={showPreApprovalModal === 'quick'}
        onOpenChange={(v) => setShowPreApprovalModal(v ? 'quick' : false)}
        villaNumber={user?.villa_number}
        onCreated={(pass) => { loadData(); setSelectedGatePass(pass); setShowQRModal(true); }}
      />
      <DeliveryPassModal
        open={showPreApprovalModal === 'delivery'}
        onOpenChange={(v) => setShowPreApprovalModal(v ? 'delivery' : false)}
        villaNumber={user?.villa_number}
        onCreated={(pass) => { loadData(); setSelectedGatePass(pass); setShowQRModal(true); }}
      />
      <FrequentPassModal
        open={showPreApprovalModal === 'frequent'}
        onOpenChange={(v) => setShowPreApprovalModal(v ? 'frequent' : false)}
        villaNumber={user?.villa_number}
        onCreated={(pass) => { loadData(); setSelectedGatePass(pass); setShowQRModal(true); }}
      />
      <PartyInviteModal
        open={showPreApprovalModal === 'party'}
        onOpenChange={(v) => setShowPreApprovalModal(v ? 'party' : false)}
        villaNumber={user?.villa_number}
        onCreated={() => loadData()}
      />
      <GatePassModals
        showPreApprovalModal={false}
        setShowPreApprovalModal={() => {}}
        preApprovalForm={preApprovalForm}
        setPreApprovalForm={setPreApprovalForm}
        handleCreatePreApproval={handleCreatePreApproval}
        showQRModal={showQRModal}
        setShowQRModal={setShowQRModal}
        selectedGatePass={selectedGatePass}
        generateQRData={generateQRData}
        downloadQRCode={downloadQRCode}
        shareGatePass={shareGatePass}
        user={user}
      />

      <PaymentModal
        showPaymentModal={showPaymentModal}
        setShowPaymentModal={setShowPaymentModal}
        selectedPayment={selectedPayment}
        processPayment={processPayment}
      />

      <BookingModal
        showBookingModal={showBookingModal}
        handleCloseBookingModal={handleCloseBookingModal}
        selectedFacility={selectedFacility}
        bookingForm={bookingForm}
        setBookingForm={setBookingForm}
        handleConfirmBooking={handleConfirmBooking}
      />

      <UPIPaymentModal
        showUPIModal={showUPIModal}
        setShowUPIModal={setShowUPIModal}
        selectedInvoice={selectedInvoice}
        setSelectedInvoice={setSelectedInvoice}
        upiLinks={upiLinks}
        setUpiLinks={setUpiLinks}
        paymentProcessing={paymentProcessing}
        handleConfirmUPIPayment={handleConfirmUPIPayment}
        openUPIApp={openUPIApp}
        copyUPIId={copyUPIId}
      />

      <ReceiptsModal
        showReceiptsModal={showReceiptsModal}
        setShowReceiptsModal={setShowReceiptsModal}
        filteredReceipts={filteredReceipts}
        receiptFilter={receiptFilter}
        setReceiptFilter={setReceiptFilter}
        months={months}
        years={years}
      />

      <VisitorsModal
        showVisitorsModal={showVisitorsModal}
        setShowVisitorsModal={setShowVisitorsModal}
        visitors={visitors}
        onRefresh={loadData}
      />

      <NoticeModals
        showNoticesModal={showNoticesModal}
        setShowNoticesModal={setShowNoticesModal}
        notices={notices}
        selectedNotice={selectedNotice}
        setSelectedNotice={setSelectedNotice}
      />

      <PaymentsDueModal
        showPaymentsDueModal={showPaymentsDueModal}
        setShowPaymentsDueModal={setShowPaymentsDueModal}
        pendingInvoices={pendingInvoices}
        totalDue={totalDue}
        handlePayInvoice={handlePayInvoice}
      />

      <StaffModal
        showStaffModal={showStaffModal}
        setShowStaffModal={setShowStaffModal}
        myStaff={myStaff}
        activeStaffOnCampus={activeStaffOnCampus}
      />

      <InvoicesModal
        showInvoicesModal={showInvoicesModal}
        setShowInvoicesModal={setShowInvoicesModal}
        filteredInvoices={filteredInvoices}
        invoiceFilter={invoiceFilter}
        setInvoiceFilter={setInvoiceFilter}
        months={months}
        years={years}
        totalDue={totalDue}
        paidInvoices={paidInvoices}
        handlePayInvoice={handlePayInvoice}
      />

      <FacilitiesModal
        showFacilitiesModal={showFacilitiesModal}
        setShowFacilitiesModal={setShowFacilitiesModal}
        facilities={facilities}
        bookingNotifications={bookingNotifications}
        handleBookFacility={handleBookFacility}
      />

      <AddCommunityModal
        showAddCommunityModal={showAddCommunityModal}
        setShowAddCommunityModal={setShowAddCommunityModal}
        communitySearch={communitySearch}
        handleSearchCommunity={handleSearchCommunity}
        communityResults={communityResults}
        handleRequestJoinCommunity={handleRequestJoinCommunity}
      />

      <MCListModal open={showMCModal} onOpenChange={setShowMCModal} />
      <NeighborsDirectoryModal open={showNeighborsModal} onOpenChange={setShowNeighborsModal} />
      <SurveysModal open={showSurveysModal} onOpenChange={setShowSurveysModal} />
      <ChatModal open={showChatModal} onOpenChange={setShowChatModal} currentUserId={user?.id} />
      <HelpdeskModal open={showHelpdeskModal} onOpenChange={setShowHelpdeskModal} role="resident" />
      <RecommendationsModal open={showRecommendationsModal} onOpenChange={setShowRecommendationsModal} currentUserId={user?.id} />
    </div>
  );
}
